import React from 'react';
import Head from 'next/head';
import {useRouter} from 'next/router';

import {BASE_URL, TWITTER_HANDLE} from '../constants/config';
import {getBrandName, TITLE_FKA_SUFFIX_ENABLED} from './Brand';

const DocumentHeader = ({
  imgAlt,
  imgPath = 'images/aced_social_thumbnail.png',
  isFree = true,
  metaDescription,
  socialDescription,
  title,
  canonicalUrl,
  ssrCanonical = false,
  noindex = false,
  prerender404 = false, // Return 404 to crawlers: https://docs.prerender.io/docs/404-checker
}) => {
  const router = useRouter();

  const appendedIsAccessibleForFree = {
    isAccessibleForFree: isFree,
    hasPart: {
      '@type': 'WebPageElement',
      isAccessibleForFree: isFree,
      cssSelector: '.paywall',
    },
  };

  const fullTitle = title ? `${title} - ${getBrandName({fka: TITLE_FKA_SUFFIX_ENABLED})}` : null;

  // imgPath can be relative or absolute
  const imgUrl = imgPath ? new URL(imgPath, BASE_URL) : null;

  // On the server, Next's router is ready only for pages that fetch per
  // request; statically generated pages render without the query string, so a
  // canonical derived from asPath has to wait for the client. A page whose
  // canonical comes from its own data rather than asPath passes ssrCanonical,
  // so crawlers that are not served a prerendered snapshot still get it.
  const canonicalReady = Boolean(canonicalUrl) && (router.isReady || ssrCanonical);

  return (
    <Head>
      {fullTitle && <title key="title">{fullTitle}</title>}
      <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no, user-scalable=no" key="viewport" />
      {metaDescription && <meta name="description" content={metaDescription} key="description" />}

      {canonicalReady ? <link rel="canonical" href={canonicalUrl} key="canonical" /> : null}

      {/* OG tags */}
      <meta property="og:type" content="website" key="og:type" />
      <meta property="og:site_name" content={getBrandName({fka: true})} key="og:site_name" />
      {fullTitle && <meta property="og:title" content={fullTitle} key="og:title" />}
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} key="og:url" />}
      {imgUrl && <meta property="og:image" content={imgUrl} key="og:image" />}
      {imgAlt && <meta property="og:image:alt" content={imgAlt} key="og:image:alt" />}
      {socialDescription && <meta property="og:description" content={socialDescription} key="og:description" />}

      {/* Twitter tags */}
      <meta name="twitter:card" content="summary_large_image" key="twitter:card" />
      <meta name="twitter:site" content={TWITTER_HANDLE} key="twitter:site" />
      {canonicalUrl && <meta name="twitter:url" content={canonicalUrl} key="twitter:url" />}
      {fullTitle && <meta name="twitter:title" content={fullTitle} key="twitter:title" />}
      {socialDescription && <meta property="twitter:description" content={socialDescription} key="twitter:description" />}
      {imgUrl && <meta property="twitter:image" content={imgUrl} key="twitter:image" />}
      {imgAlt && <meta property="twitter:image:alt" content={imgAlt} key="twitter:image:alt" />}

      {/* web-crawler flags */}
      {noindex
        ? <meta name="robots" content="noindex" key="noindex" />
        : null}
      {isFree
        ? null
        : <meta name="robots" content="noarchive" key="noarchive" />}
      {prerender404
        ? <meta name="prerender-status-code" content="404" />
        : null}

      {/* JSON-LD for structured data */}
      <script type="application/ld+json" key="paywall">
        {JSON.stringify(appendedIsAccessibleForFree)}
      </script>
    </Head>
  );
};

export default DocumentHeader;
